import React, {
  useMemo,
  useEffect,
  useCallback,
  PropsWithChildren,
  ReactNode,
} from 'react';
import _ from 'lodash';
import c from 'classnames';
import RCTable from 'rc-table';
import { TableProps } from 'rc-table/lib/Table';
import { ColumnType } from 'rc-table/lib/interface';

import Pagination from '../Pagination/Pagination';

import useSort, { SortConfig } from './useSort';
import usePagination from './usePagination';
import ExpandIcon from './ExpandIcon';

import './Table.styl';

export interface Column<RecordType = any> extends ColumnType<RecordType> {
  sorter?: string | ((record: RecordType, direction: 'asc' | 'desc') => any);
  children?: Column<RecordType>[];
  title: ReactNode | ((props: { sorterNode: ReactNode }) => ReactNode);
}

export interface Props<RecordType = any> extends TableProps<RecordType> {
  columns?: Column<RecordType>[];
  className?: string;
  pagination?: 'front-end' | 'back-end' | null;
  pageSize?: number;
  pageSizeOptions?: number[];
  pageCount?: number | null;
  paginationWithSearch?: boolean;
  currentPageNumber?: number | null; // back-end use  Or  Front-end use
  onPageChange?: ((page: number) => void | Promise<void>) | null;
  onPageSizeChange?: ((pageSize: number) => void | Promise<void>) | null;
  onChangeSort?: (sortConfig: SortConfig | null) => void | Promise<void>; // back-end sorter
}

const prefixCls = 'mio-table-next';

const Table = <RecordType extends Record<string, any>>({
  data = [],
  columns = [],
  className = '',
  rowKey = 'key',
  expandable = {},
  pagination = null,
  pageSize = 10,
  pageCount = null,
  paginationWithSearch = true,
  currentPageNumber = null,
  onPageChange = null,
  onChangeSort = () => {},
  pageSizeOptions,
  onPageSizeChange,
  ...props
}: PropsWithChildren<Props<RecordType>>) => {
  // sort
  const { sortConfig, sortedData, handleSortColumn } = useSort({ data: data as any });

  useEffect(() => {
    onChangeSort(sortConfig);
  }, [sortConfig, onChangeSort]);

  // pagination
  const {
    currentPage,
    totalPage,
    setCurrentPage,
    currentPageData,
  } = usePagination({
    data: sortedData,
    pagination,
    pageSize,
    pageCount,
    currentPageNumber,
    onPageChange,
  });

  // row class
  const getRowClassName = useCallback((_record, index) => {
    if (index % 2) {
      return 'mio-table-next-row-even';
    }
    return 'mio-table-next-row-odd';
  }, []);

  const renderSort = useCallback(
    (column: Column<RecordType>) => {
      return (
        <div
          className={`${prefixCls}-sort-icons`}
          onClick={() => handleSortColumn(column.key, column.sorter)}
        >
          <div
            className={c(`${prefixCls}-sort-icons-inner`, {
              [`${prefixCls}-sort-icons-asc`]:
                sortConfig &&
                sortConfig.key === column.key &&
                sortConfig.direction === 'asc',
              [`${prefixCls}-sort-icons-desc`]:
                sortConfig &&
                sortConfig.key === column.key &&
                sortConfig.direction === 'desc',
            })}
          >
            <i className="iconfont icon-sort-up" />
            <i className="iconfont icon-sort-down" />
          </div>
        </div>
      );
    },
    [handleSortColumn, sortConfig],
  );

  const renderTitle = useCallback(
    (column: Column<RecordType>) => {
      if (_.isFunction(column.title)) {
        return column.title({ sorterNode: renderSort(column) });
      }
      return (
        <>
          <span>{column.title}</span>
          {column.sorter && renderSort(column)}
        </>
      );
    },
    [renderSort],
  );

  const _columns = useMemo(() => {
    const mapColumn = (
      column: Column<RecordType>,
      index: number,
      isChildren: boolean,
    ): Column<RecordType> => {
      const showBorder = isChildren && index === 0;
      return {
        ...column,
        className: Boolean(column.children)
          ? `${column.className} ${prefixCls}-column-group`
          : column.className,
        title: renderTitle(column),
        children: column.children
          ? column.children.map((column, index) =>
              mapColumn(column, index, true),
            )
          : undefined,
        onCell: (...args) => {
          const { className = '', ...rest } = column.onCell
            ? column.onCell(...args)
            : {};
          return {
            ...rest,
            className: showBorder
              ? `${prefixCls}-cell-border-left ${className}`
              : className,
          };
        },
        onHeaderCell: (...args) => {
          const { className = '', ...rest } = column.onHeaderCell
            ? column.onHeaderCell(...args)
            : {};
          return {
            ...rest,
            className: showBorder
              ? `${prefixCls}-cell-border-left ${className}`
              : className,
          };
        },
      };
    };

    return columns.map((column, index) => mapColumn(column, index, false));
  }, [columns, renderTitle]);

  const hasChildren = columns.some(column => 'children' in column);

  return (
    <div>
      <RCTable<RecordType>
        rowKey={rowKey}
        prefixCls={prefixCls}
        data={currentPageData}
        columns={_columns}
        rowClassName={getRowClassName}
        className={c(className, {
          [`${prefixCls}-has-children`]: hasChildren,
        })}
        expandable={{
          expandIcon: _props => <ExpandIcon<RecordType> {..._props} />,
          ...expandable,
        }}
        {...props}
      />

      {pagination === 'front-end' && (
        <Pagination
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={onPageSizeChange}
          pageCount={totalPage}
          currentPage={currentPage}
          onSelectPage={setCurrentPage}
          withSearch={paginationWithSearch}
        />
      )}

      {pagination === 'back-end' && currentPageNumber !== null && (
        <Pagination
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={onPageSizeChange}
          pageCount={totalPage}
          currentPage={currentPageNumber}
          onSelectPage={setCurrentPage}
          withSearch={paginationWithSearch}
        />
      )}
    </div>
  );
};

const typedMemo: <T>(c: T) => T = React.memo;

const MemoTable = typedMemo(Table);

export default MemoTable;
